import React, { useEffect, useState, useRef, useCallback } from 'react';
import { mountIsland } from '../../mountIsland';
import { CircularProgress } from '@mui/material';
import { __ } from '../../Lang.js';
import Button from '@mui/material/Button';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import Link from '@mui/material/Link';
import { isEqual } from 'lodash';


// Import components
import SearchBar from '../common/SearchBar.jsx';
import FilterControls from '../common/FilterControls.jsx';
import VideoGrid from '../common/VideoGrid.jsx';
import InternalNav from '../common/InternalNav';
import InfiniteScrollContainer from '../common/InfiniteScrollContainer.tsx';

const Results = () => {

    // Constants
    const DEFAULT_FILTER_STATE = {
        all: true,
        audio: false,
        video: false,
        accessAll: true,
        public: false,
        personal: false,
        premium: false,
        open: false,
        usageAll: true,
        minister: false,
        initiate: false,
        presenterAll: true,
        johnroger: false,
        johnmorton: false
    };

    const searchParams = new URLSearchParams(window.location.search);

    // global data reference
    const initialDataRef = useRef([]);

    // State management
const [videos, setVideos] = useState([]);
const [playlists, setPlaylists] = useState([]);
const [allVideos, setAllVideos] = useState([]);
const [loading, setLoadingOrig] = useState(true);
const setLoading = (value) => {
  setLoadingOrig(value);
};


    const [isFetchingMore, setIsFetchingMore] = useState(false);
const [hasMorePages, setHasMorePages] = useState(true);
    // Search state
    const [defaultSearchQuery, setDefaultSearchQuery] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
const [totalPages, setTotalPagesOrig] = useState(1);
const setTotalPages = (value) => {
  setTotalPagesOrig(value);
};
    
    const [totalCount, setTotalCount] = useState(0);
    
    // Content metadata
    const [playlistTag, setPlaylistTag] = useState('');
    const [playlistTagUniqid, setPlaylistTagUniqid] = useState('');

    const [selectedYear, setSelectedYear] = useState('');
    const [availableYears, setAvailableYears] = useState([]);
    const [isSATMode, setIsSATMode] = useState(false);

    const [playlistName, setPlaylistName] = useState('');
    const [isLoggedIn, setIsLoggedIn] = useState(false);

    const [isEditingPlaylist, setIsEditingPlaylist] = useState(false);
    const [orderedVideos, setOrderedVideos] = useState([]);
    
    const [filterActive, setFilterActive] = useState(DEFAULT_FILTER_STATE);

    const [isMobile, setIsMobile] = useState(false);

    const isCustomPlaylist = searchParams.has('mylist');
    const [pageTitle, setPageTitle] = useState('');

    useEffect(() => {
    if (isCustomPlaylist && videos.length > 0) {
        // Filter out any null/undefined videos from bad data before mapping to IDs
        setOrderedVideos(videos.filter(v => v && v.id).map(v => v.id));
    }
    }, [isCustomPlaylist, videos]);


    // Effects
    useEffect(() => {
        const listParam = searchParams.get('list');
        if (listParam) {
            setPlaylistTag(listParam);
        }
        const usageValue = searchParams.get('usage') || searchParams.get('usage_type');
        let tagValue = searchParams.get('tag');

        // Map usage to corresponding tag as fallback
        if (usageValue === 'minister') tagValue = tagValue || 'Ministers Only';
        if (usageValue === 'initiate') tagValue = tagValue || 'Initiates Only';

        const context = {
            list: searchParams.get('list'),
            tag: tagValue,
            type: searchParams.get('type'),
            category: searchParams.get('category'),
            usage: usageValue,
        };
    }, []);



useEffect(() => {
  const usageParam = searchParams.get('usage_type');
  const queryParam = searchParams.get('search') ?? '';
  if (queryParam) {
    setDefaultSearchQuery(queryParam);
  }
  
  if (usageParam) {
    // Instead of calling directFilterByUsage, update filter state
    const newFilterState = {
      ...DEFAULT_FILTER_STATE,
      usageAll: false,
      personal: usageParam === 'personal',
      public: usageParam === 'public',
      minister: usageParam === 'minister',
      initiate: usageParam === 'initiate'
    };
    setFilterActive(newFilterState);
    // The initial data should already be filtered by the backend
  }
}, []);


   
    useEffect(() => {
  const resultsElement = document.getElementById('videos-results');
  if (!resultsElement) return;

  const pageParam = parseInt(searchParams.get('page') ?? '1', 10);
  const initialDataAttr = resultsElement.getAttribute('data-initial-data');
  setDefaultSearchQuery(resultsElement.getAttribute('data-search') ?? '');

  if (initialDataAttr) {
    try {
      const initialData = JSON.parse(initialDataAttr);
      initialDataRef.current = initialData.videos ?? [];
      setIsLoggedIn(initialData.logged_in_user ?? false);

      const typeParam = searchParams.get('type');
      const usageParam = searchParams.get('usage_type');
      const isSAT = typeParam === 'sats' || usageParam === 'sat';
      setPageTitle(__(initialData.group_type) + ': ' + initialData.result_parameter || '');
      setIsSATMode(isSAT);

      if (isSAT && initialData.years) {
        setAvailableYears(initialData.years);
      }

      const yearParam = searchParams.get('year');
      if (yearParam && isSAT) {
        setSelectedYear(yearParam);
      }

      let totalCount = initialData.pagination?.total ?? 0;
      totalCount += initialData.playlists?.length ?? 0;

      filterVideosAndDisplay(
        initialData.videos ?? [],
        totalCount,
        initialData.pagination?.total ?? 1,
        initialData.pagination?.page ?? pageParam,
        initialData.playlists ?? [],
      );

      setPlaylistTag(initialData.result_parameter ?? '');
      setPlaylistTagUniqid(initialData.parameter_uniqid ?? '');
      setPlaylistName(initialData.playlist_name ?? '');
      setLoading(false);
    } catch (error) {
      console.error('Error parsing initial data:', error);
      setLoading(false);
    }
  }
}, []);

useEffect(() => {
  if (initialDataRef.current.videos?.length < initialDataRef.current.pagination?.per_page ) {
    setHasMorePages(false); 
  }
}, [initialDataRef.current]); 


    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth < 768);
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Functions
    

const getFilterParams = (filterState) => {
  const params = {};
  
  if (!filterState.all) {
    if (filterState.audio) params.asset_type = 'audio';
    if (filterState.video) params.asset_type = 'video';
  }

  if (!filterState.usageAll) {
    if (filterState.personal) params.usage_type = 'personal';
    if (filterState.public) params.usage_type = 'public';
    if (filterState.minister) params.usage_type = 'minister';
    if (filterState.initiate) params.usage_type = 'initiate';
  }

  if (!filterState.presenterAll) {
    if (filterState.johnroger) params.presenter = 'john-roger';
    if (filterState.johnmorton) params.presenter = 'john morton';
  }

  if (!filterState.accessAll) {
    if (filterState.premium) params.access = 'premium';
    if (filterState.open) params.access = 'open';
  }
  
  return params;
};


    const updateGrid = useCallback((videos, total, total_pages, page, playlists) => {
        const filteredVideos = applyAllActiveFilters(videos, filterActive);
        let newTotalCount = total ?? filteredVideos.length;
        newTotalCount += playlists?.length ?? 0;
        filterVideosAndDisplay(filteredVideos, newTotalCount, total_pages, page, playlists);
    }, [filterActive]);

const updateGridVideos = (
  videos,
  totalCount,
  totalPages,
  currentPage,
  resetAllVideos = false
) => {
  // Add this line at the very top
  if (totalPages === 1) {
    console.warn('Warning: totalPages is set to 1, which may not be expected. This could be due to the initial data or API response.');
}


  if (resetAllVideos) {
    setAllVideos(videos ?? []);
  }

  setVideos(videos ?? []);
  setTotalCount(totalCount ?? 0);
  setTotalPages(totalPages ?? 1);

  setCurrentPage(currentPage ?? 1);
  setHasMorePages((currentPage ?? 1) < (totalPages ?? 1));
};

const loadNextPage = () => {
  if (isFetchingMore || !hasMorePages) return;

  const nextPage = currentPage + 1;
  setIsFetchingMore(true);

  // Get CURRENT URL params instead of using stale searchParams
  const currentParams = new URLSearchParams(window.location.search);
  const filterParams = getFilterParams(filterActive);
  
  const queryString = new URLSearchParams({
    ...Object.fromEntries(currentParams.entries()),
    ...filterParams,
    page: nextPage
  }).toString();

  fetch(`/api/v2/twi/results?${queryString}`)
    .then(res => res.json())
    .then((data) => {
      if (data.status === 'success') {
        setVideos(prev => [...prev, ...data.videos]);
        setAllVideos(prev => [...prev, ...data.videos]);
        setCurrentPage(nextPage);
        setHasMorePages(nextPage < data.pagination?.total_pages);
      }
    })
    .finally(() => {
      setIsFetchingMore(false);
    });
};

const fetchWithFilters = async (newFilterState) => {
  setLoading(true);
  
  try {
    // Get CURRENT URL params instead of using stale searchParams
    const currentParams = new URLSearchParams(window.location.search);
    const filterParams = getFilterParams(newFilterState);
    
    const queryString = new URLSearchParams({
      ...Object.fromEntries(currentParams.entries()),
      ...filterParams,
      page: 1 // Reset to page 1
    }).toString();

    const res = await fetch(`/api/v2/twi/results?${queryString}`);
    const data = await res.json();
    
    if (data.status === 'success') {
      setVideos(data.videos || []);
      setAllVideos(data.videos || []);
      setCurrentPage(1);
      setTotalPages(data.pagination?.total_pages || 1);
      setTotalCount(data.pagination?.total || 0);
      setHasMorePages(1 < (data.pagination?.total_pages || 1));

      if (data.playlists) {
        setPlaylists(data.playlists.map(derivePlaylistTraits));
      }

      // Store tag uniqid if present
      if (data.parameter_uniqid) {
        setPlaylistTagUniqid(data.parameter_uniqid);
      }
    }
  } catch (err) {
    console.error('Error fetching filtered data:', err);
  } finally {
    setLoading(false);
  }
};

    const applyAllActiveFilters = (videos, state) => {
        // Normalize each item's filter traits from its first media item so
        // playlists (audiobooks / collections) filter the same as plain videos.
        let result = videos.map(derivePlaylistTraits);

        // Apply content type
        result = applyContentTypeFilter(result, state);

        // Apply usage/access filters
        if (!state.accessAll) {
        if (state.premium) {
            result = result.filter(v => v.access === 'svod');
        } else if (state.open) {
            result = result.filter(v => v.access !== 'svod');
        }
        }

        if (!state.usageAll) {
            if (state.minister) {
                result = result.filter(v => {
                const ut = v.usage_type?.toLowerCase();
                return ut === 'minister' || ut === 'minster or initiate';
                });
            } else if (state.initiate) {
                result = result.filter(v => {
                const ut = v.usage_type?.toLowerCase();
                return ut === 'initiate' || ut === 'minster or initiate';
                });
            } else if (state.personal) {
                result = result.filter(v => v.usage_type?.toLowerCase() === 'personal');
            } else if (state.public) {
                result = result.filter(v => v.usage_type?.toLowerCase() === 'public');
            }
        }

        if (!state.presenterAll) {
            if (state.johnroger) {
                result = result.filter(v => v.presenter?.toLowerCase() === 'john-roger');
            } else if (state.johnmorton) {
                result = result.filter(v => v.presenter?.toLowerCase() === 'john morton');
            }
        }

        // Remove duplicates
        return Array.from(new Map(result.map(v => [v.id, v])).values());
    };

    /**
     * Iterate through the videos, filtering out any videos that are in a playlist 
     * that also appears in the playlist search results. Then display both the videos and playlists.
     * 
     */
    const filterVideosAndDisplay = (videos, total, total_pages, page = 1, playlists = []) => {
        let videos_count_pre = videos.length;

        videos = videos.filter(video => {
            if (!video.playlists || video.playlists.length === 0) return true;
            if (!playlists || playlists.length === 0) return true;

            // check if any of the video's playlists are in the search results
            return !video.playlists.some(videoPlaylist => {
                return playlists.some(playlist => {
                    if (playlist.name.toLowerCase() === videoPlaylist.toLowerCase())
                    {
                        // if the playlist is in the search results, we need to set the preview image url
                        playlist.preview_image_url = playlist.preview_image_url ?? video.preview_image_url;
                        return true;
                    }
                    return false;
                });
            });
        });
        
        //reduce the total count by the number of videos that were filtered out
        // here we assume all videos that would be in a playlist would be on the first page
        total -= (videos_count_pre - videos.length);

        // callback to parent
        updateGridVideos(
            videos ?? [],
            total ?? 0,
            total_pages,
            page ?? 1,
            true // reset allVideos to the new videos
        );

        setPlaylists(
            playlists ?? []
        )
    }





    // Pure persistence — PUT the full ordered video id list to the v2 reorder
    // endpoint, return success boolean, no UI side effects. Both the explicit
    // Save button (savePlaylistOrder) and the drag-end auto-save
    // (autoSavePlaylistOrder, wired into VideoGrid via the onReorder prop) call
    // this so we have a single source of truth for the network call.
    const persistPlaylistOrder = async (newOrder) => {
        // Custom playlists use 'mylist' parameter, not 'list'
        const playlistId = searchParams.get('mylist');
        if (!playlistId || newOrder.length === 0) {
            console.warn('Missing playlist ID or no videos to save');
            return false;
        }

        try {
            // Reorder by uniqid — playlist by uniqid and each video by its uniqid.
            // newOrder holds numeric ids (local state); map them to uniqids for the API.
            const res = await fetch(`/api/v2/twi/mylist/${encodeURIComponent(playlistId)}/reorder`, {
                method: 'PUT',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
                },
                body: JSON.stringify({ playlist: newOrder.map(id => videos.find(v => v.id === id)?.uniqid).filter(Boolean) }),
            });

            if (!res.ok) {
                const responseData = await res.json().catch(() => ({}));
                throw new Error(`HTTP ${res.status}: ${responseData.message || 'Unknown error'}`);
            }
            return true;
        } catch (err) {
            console.error('Failed to persist playlist order:', err);
            return false;
        }
    };

    // Auto-save fired by VideoGrid after each drag end. Silent on success so
    // the user keeps editing without interruption; logs on failure and lets
    // the user fall back to the explicit Save button to retry.
    const autoSavePlaylistOrder = async (newOrder) => {
        const ok = await persistPlaylistOrder(newOrder);
        if (!ok) {
            console.warn('Auto-save failed; use the Save button to retry.');
        }
    };

    // Explicit Save click — persist + exit editing mode + user-visible toast.
    const savePlaylistOrder = async () => {
        const ok = await persistPlaylistOrder(orderedVideos);
        if (ok) {
            const reordered = orderedVideos.map(id => videos.find(v => v.id === id)).filter(Boolean);
            setVideos(reordered);
            setIsEditingPlaylist(false);
            alert(__('Playlist order saved successfully!'));
        } else {
            alert(__('Failed to save playlist. Please try again.'));
        }
    };


    const handleDeleteVideoFromPlaylist = async (videoId) => {
        // Custom playlists use 'mylist' parameter, not 'list'
        let playlistId = searchParams.get('mylist');
        if (!playlistId) return;

        // Video addressed by uniqid; local state still keys on the numeric id.
        const videoUniqid = videos.find(v => v.id === videoId)?.uniqid;
        if (!videoUniqid) return;

        try {
            let url = `/api/v2/twi/mylist/${playlistId}/video/${encodeURIComponent(videoUniqid)}`;
            let res = await fetch(url, { method: 'DELETE' });

            if (!res.ok) throw new Error('Failed to delete');

            // Update state
            setOrderedVideos(prev => prev.filter(id => id !== videoId));
            setVideos(prev => prev.filter(v => v.id !== videoId));
        } catch (err) {
            console.error('❌ Error deleting video from playlist:', err);
        }
    };

    /**
     * Playlists (e.g. audiobooks / collections) don't carry the scalar
     * presenter / asset_type (content type) / usage_type / access traits that
     * FilterControls' filtering logic reads off each item. Derive them from the
     * playlist's FIRST media item so the filters work. This is the frontend-only
     * derivation; a paired backend model change is tracked separately.
     *
     * Returns the item with the four scalar trait fields populated. If the item
     * already exposes scalar traits at the top level (a compacted-video
     * playlist), those are preserved. Playlists with zero media items are
     * returned unchanged (guarded).
     */
    const derivePlaylistTraits = (item) => {
        if (!item || typeof item !== 'object') return item;

        // Locate the first media item across the field names the API may use.
        const mediaItems = item.videos ?? item.media ?? item.items ?? null;
        const firstMedia = Array.isArray(mediaItems) ? mediaItems[0] : null;

        // Guard: zero media items and no scalar fallback -> leave as-is.
        if (!firstMedia && item.presenter === undefined && item.asset_type === undefined
            && item.usage_type === undefined && item.access === undefined) {
            return item;
        }

        // Some contracts expose traits as arrays aggregated across media; in
        // that case take the first element as the representative scalar value.
        const firstScalar = (value) => (Array.isArray(value) ? value[0] : value);

        const presenter  = firstMedia?.presenter  ?? firstScalar(item.presenter);
        const asset_type = firstMedia?.asset_type ?? firstScalar(item.asset_type);
        const usage_type = firstMedia?.usage_type ?? firstScalar(item.usage_type);
        // access has no aggregate equivalent on group contracts; fall back to
        // the group's visibility ('SVOD Only' -> 'svod') when no scalar exists.
        const access = firstMedia?.access ?? item.access
            ?? (item.visibility === 'SVOD Only' ? 'svod' : item.visibility);

        return { ...item, presenter, asset_type, usage_type, access };
    };

    // Filter functions
    const applyContentTypeFilter = (videos, filterState) => {
        if (filterState.all) return videos;
        if (filterState.audio) return videos.filter(v => v.asset_type?.toLowerCase() === 'audio');
        if (filterState.video) return videos.filter(v => v.asset_type?.toLowerCase() === 'video');
        return videos;
    };


const handleFilterClick = (filter) => {
  let newFilterState = { ...filterActive };
  if (filter == 'clear') {
    newFilterState = DEFAULT_FILTER_STATE;
  }

  // Content type filters
  if (['all', 'audio', 'video'].includes(filter)) {
    newFilterState.all = filter === 'all';
    newFilterState.audio = filter === 'audio';
    newFilterState.video = filter === 'video';
  }
  // Usage filters
  else if (['usageAll', 'personal', 'public', 'minister', 'initiate'].includes(filter)) {
    newFilterState.usageAll = filter === 'usageAll';
    newFilterState.personal = filter === 'personal';
    newFilterState.public = filter === 'public';
    newFilterState.minister = filter === 'minister';
    newFilterState.initiate = filter === 'initiate';
  }
  // Presenter filters
  else if (['presenterAll', 'johnroger', 'johnmorton'].includes(filter)) {
    newFilterState.presenterAll = filter === 'presenterAll';
    newFilterState.johnroger = filter === 'johnroger';
    newFilterState.johnmorton = filter === 'johnmorton';
  }
  // Access filters
  else if (['accessAll', 'premium', 'open'].includes(filter)) {
    newFilterState.accessAll = filter === 'accessAll';
    newFilterState.premium = filter === 'premium';
    newFilterState.open = filter === 'open';
  }

  // Update filter state
  setFilterActive(newFilterState);

  // Update URL with new filters
  updateURLWithFilters(newFilterState);

  // Audiobooks / collections are returned by the API as a single full list of
  // playlist items and are NOT filtered server-side, so filter them client-side
  // from the pristine item set using the traits derived from each playlist's
  // first media item. All other types continue to re-fetch from the server.
  const resultType = searchParams.get('type');
  if (resultType === 'audiobooks' || resultType === 'collections') {
    const source = initialDataRef.current?.length ? initialDataRef.current : allVideos;
    const filtered = applyAllActiveFilters(source, newFilterState);
    updateGridVideos(filtered, filtered.length, 1, 1, false);
    return;
  }

  // Fetch fresh data with new filters
  fetchWithFilters(newFilterState);

};



const updateURLWithFilters = (filterState) => {
  const currentParams = new URLSearchParams(window.location.search);
  const baseParams = Object.fromEntries(currentParams.entries());
  const filterParams = getFilterParams(filterState);
  
  //delete baseParams.type;
  delete baseParams.usage_type;
  delete baseParams.asset_type;
  delete baseParams.presenter;
  delete baseParams.access;
  
  const newParams = new URLSearchParams({
    ...baseParams,
    ...filterParams
  });
  
  newParams.delete('page');
  
  const newUrl = `${window.location.pathname}?${newParams.toString()}`;
  window.history.replaceState({}, '', newUrl);
};


const fetchDataWithYear = async (year) => {
  setLoading(true);
  
  try {
    // Create fresh URLSearchParams from current window location
    const currentParams = new URLSearchParams(window.location.search);
    
    if (year) {
      currentParams.set('year', year);
    } else {
      currentParams.delete('year');
    }
    
    // ADD: Include current filter state
    const filterParams = getFilterParams(filterActive);
    Object.entries(filterParams).forEach(([key, value]) => {
      currentParams.set(key, value);
    });
    
    const apiUrl = `/api/v2/twi/results?${currentParams.toString()}`;
    
    const res = await fetch(apiUrl);
    const data = await res.json();
    
    if (data.status === 'success') {
      setAllVideos(data.videos || []);
      setVideos(data.videos || []);
      setTotalPages(data.pagination?.total_pages || 1);
      setTotalCount(data.pagination?.total || 0);
      setCurrentPage(data.pagination?.page || 1);
      setHasMorePages((data.pagination?.page || 1) < (data.pagination?.total_pages || 1));

      // Update available years if provided in response
      if (data.years && Array.isArray(data.years)) {
        setAvailableYears(data.years);
      }

      // Store tag uniqid if present
      if (data.parameter_uniqid) {
        setPlaylistTagUniqid(data.parameter_uniqid);
      }

      // Only update URL after successful API response
      const newUrl = `${window.location.pathname}?${currentParams.toString()}`;
      window.history.replaceState({}, '', newUrl);
    }
  } catch (err) {
    console.error('Error fetching data with year filter:', err);
  } finally {
    setLoading(false);
  }
};



const handleYearChange = (year) => {
    // Update the state immediately for UI feedback
    setSelectedYear(year);
    
    // Fetch new data with the year filter (this will also update the URL)
    fetchDataWithYear(year);
};

const handleDeleteEntirePlaylist = async () => {
  // Custom playlists use 'mylist' parameter, not 'list'
  const playlistId = searchParams.get('mylist');
  if (!playlistId) return;

  if (!confirm('Are you sure you want to delete this playlist? This cannot be undone.')) return;

  try {
    // Delete by uniqid (never numeric playlist_id).
    const res = await fetch(`/api/v2/twi/mylist/${encodeURIComponent(playlistId)}`, {
      method: 'DELETE',
      headers: {
        'Accept': 'application/json',
        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
      },
    });

    if (!res.ok) throw new Error(`HTTP ${res.status}`);

    // Redirect or refresh page after deletion
    window.location.href = '/app/twi/results';
  } catch (err) {
    console.error(' Failed to delete playlist:', err);
    alert('There was an error deleting the playlist.');
  }
};

const getPageTitle = () => {
    if (searchQuery || defaultSearchQuery) {
        return `${__('Search Results for')}: "${searchQuery || defaultSearchQuery}"`;
    }

    // Check for custom playlist FIRST (highest priority)
    if (isCustomPlaylist && playlistName) {
        return playlistName;
    }

    // Get current context from URL params and state
    const contextType = searchParams.get('type') || '';
    const contextList = searchParams.get('list') || '';
    const contextCParam = searchParams.get('c') || '';  // v2 category param (uniqid)
    const contextCategoryParam = searchParams.get('category') || '';  // v1 category param (deprecated)
    const contextTag = searchParams.get('tag') || '';
    const contextUsage = searchParams.get('usage') || searchParams.get('usage_type') || '';

    const titleMap = {
        history: __('History'),
        recent: __('Recently Added'),
        collections: __('Collections'),
        mylist: __('My List'),
        audiobooks: __('Audiobooks'),
        unplayed: __('New to Me'),
        continue: __('Continue Watching'),
        heavy: __('Frequently Watched'),
        sats: __('SAT Seminar Library'),
    };

    if (titleMap[contextType]) return titleMap[contextType];

    // For v2 category param (c=), use playlistTag from API response
    if (contextCParam) {
        // Use playlistTag if available (from API response), otherwise show loading state
        return `${__('Category')}: ${playlistTag || '...'}`;
    }

    // For v1 category param (category=), use the URL param directly
    if (contextCategoryParam) {
        return `${__('Category')}: ${decodeURIComponent(contextCategoryParam)}`;
    }

    // Check for usage parameter and use playlistName from backend
    if (contextUsage && playlistName) {
        return playlistName;
    }

    // Fallback for usage without backend name
    if (contextUsage) {
        return `${__('Usage')}: ${decodeURIComponent(contextUsage)}`;
    }

    // Use playlistName if available for any playlist context
    if ((contextType === 'playlist' || playlistTag || isCustomPlaylist) && playlistName) {
        return playlistName;
    }

    // Fallback for playlist without name
    if (contextType === 'playlist' || playlistTag || isCustomPlaylist) {
        return __('My Playlist');
    }

    return __('All Results');
};


useEffect(() => {
    if (isSATMode) {
        const urlParams = new URLSearchParams(window.location.search);
        const yearParam = urlParams.get('year');
        if (yearParam && yearParam !== selectedYear) {
            setSelectedYear(yearParam);
        }
    }
}, [isSATMode]);


    if (loading) {
        return (
            <div className="results-loading-container" sx={{ textAlign: 'center', padding: '2rem', width: '100%' }}>
                <CircularProgress className="results-spinner" sx={{ margin: "0 auto"}} />
                <p className="results-loading-text">{__('Loading videos...')}</p>
            </div>
        );
    }
    return (
        <div className="results-page">
            <div className="results-header-container">
                <div className="results-header-inner">
                    <div className="results-header-top-row">
                        <div className="results-breadcrumb">
                            <Link href="/app/twi" className="video-breadcrumb-link" color="inherit" underline="hover">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    style={{ marginRight: '6px', verticalAlign: 'middle' }}
                                >
                                    <polyline points="15 18 9 12 15 6"></polyline>
                                </svg>
                                {__('Back to TWI')}
                            </Link>
                        </div>

                    {/* Desktop sidebar layout */}
                    {!isMobile && (
                        <div className="hero__sidebar">
                            <InternalNav isLoggedIn={isLoggedIn} />
                        </div>
                    )}

                    {/* Mobile layout, matches HeroSection */}
                    {isMobile && (
                        <div className="hero__mobile-nav">
                            <InternalNav isLoggedIn={isLoggedIn} />
                        </div>
                    )}

                    </div>





                    <div className="results-title-wrapper">
                        <h1 className="results-page-title">{getPageTitle()}</h1>
                        <hr className="results-divider" />
                    </div>

                    {
                     window.location.search.includes('search=') && (
                        <SearchBar 
                            searchQuery={searchQuery}
                            defaultSearchQuery={defaultSearchQuery}
                            setSearchQuery={setSearchQuery}

                            // the callback to update the grid with videos and playlists
                            updateGrid={updateGrid}

                            // the global loading state
                            setLoading={setLoading}
                        />
                        )
                    }
                            
                    
                
                </div>
            </div>

            {/* Playlist traits (presenter/content_type/usage_type/access) are derived
                client-side from each playlist's first media item (see derivePlaylistTraits),
                so FilterControls now works for audiobooks/collections too. */}
            <FilterControls
              filterActive={filterActive}
              onFilterClick={handleFilterClick}
              showMostRelevant={!!searchQuery}
              isSATMode={isSATMode ?? false}
              selectedYear={selectedYear}
              setSelectedYear={setSelectedYear}
              availableYears={availableYears}
              onYearChange={handleYearChange}
              filtersAreInUse={ isEqual(filterActive, DEFAULT_FILTER_STATE) ? false : true }
            />

            {isCustomPlaylist && (
                <div className="edit-playlist-controls" style={{ marginTop: '1rem', marginBottom: '1rem', position: 'relative', zIndex: 1000 }}>
        {!isEditingPlaylist ? (
      <>
        <Button
          variant="outlined"
          startIcon={<EditIcon />}
          onClick={() => {
            setOrderedVideos(videos.map(v => v.id));
            setIsEditingPlaylist(true);
          }}
        >
            {__('Edit Playlist')}
          
        </Button>
        <Button
          variant="outlined"
          color="error"
          onClick={handleDeleteEntirePlaylist}
        >
          {__('Delete Playlist')}
        </Button>
      </>
        ) : (
            <>
                <Button
                    variant="contained"
                    color="success"
                    startIcon={<SaveIcon />}
                    onClick={savePlaylistOrder}
                    sx={{ mr: 1 }}
                >
                    {__('Save')}
                </Button>
                <Button
                    variant="outlined"
                    color="error"
                    startIcon={<CancelIcon />}
                    onClick={() => setIsEditingPlaylist(false)}
                >
                    {__('Cancel')}
                </Button>
            </>
        )}
                </div>
                )}

            
            <div className="results-main-section">
                {totalCount > 0 && (
                    <div className="results-count">
                        {totalCount} {__('results')}
                    </div>
                )}
                {videos.length > 0 ? (
                  <InfiniteScrollContainer
                    dataLength={videos.length}
                    hasMore={hasMorePages}
                    loadMore={loadNextPage}
                  >
                    <VideoGrid
                      videos={videos}
                      isEditingPlaylist={isEditingPlaylist}
                      orderedVideos={orderedVideos}
                      setOrderedVideos={setOrderedVideos}
                      onReorder={autoSavePlaylistOrder}
                      onDeleteVideo={handleDeleteVideoFromPlaylist}
                      groups={playlists}
                    />
                  </InfiniteScrollContainer>
                ) : null}

{!loading && videos.length === 0 && (
  <div className="results-empty-message">
    {isSATMode && selectedYear ? (
      <span
        dangerouslySetInnerHTML={{
          __html: __(
            'All SATs published during this year have been released into the <strong>Personal-Use</strong> category and are available to those not subscribed to the Soul Awareness Teachings (SAT) Series. They can be found by browsing the <strong>Seminars</strong> section or by searching by title or release number.'
          ),
        }}
      />
    ) : (
      __('No results found.')
    )}
  </div>
)}


            </div>
        </div>
    );
};

// Mount component
const videosContainer = document.getElementById('videos-results');
if (videosContainer) {
    mountIsland(videosContainer, <Results />);
}

export default Results;
